import router from "../router";
import {i18n, setI18nLocale} from '@/i18n';
import {getApiUrl} from "@/helpers/general";
import axios from "axios";
import * as Sentry from "@sentry/vue";

declare const window: any;

class Api {

    static getParameterByName(name:string) {
        const match = RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);
        return match && decodeURIComponent(match[1].replace(/\+/g, ' '));
    }

    static getBaseUrl() {
        return getApiUrl() + '/api/v2';
    }

    static fullUrl(path:string) {
        return Api.getBaseUrl() + '/' + path;
    }

    static post(path:string, body = {}) {
        return new Promise((resolve, reject) => {
            const url = this.fullUrl(path);
            const headers = {
                'Accept': 'application/json'
            };

            axios
                .post(url, body, {headers})
                .then(resolve)
                .catch(reject);
        })
    }

    static put(path:string, body = {}) {
        return new Promise((resolve, reject) => {
            const url = this.fullUrl(path);
            const headers = {
                'Accept': 'application/json',
            };

            axios
                .put(url, body, {headers})
                .then(resolve)
                .catch(reject);
        })
    }

    static delete(path:string) {
        return new Promise((resolve, reject) => {
            const url = this.fullUrl(path);
            const headers = {
                'Accept': 'application/json',
            };

            axios
                .delete(url, {headers})
                .then(resolve)
                .catch(reject);
        })
    }

    static get(path: string) {
        return new Promise((resolve, reject) => {
            const url = this.fullUrl(path);
            const headers = {
                'Accept': 'application/json',
            };

            axios
                .get(url, {headers: headers})
                .then(resolve)
                .catch(reject);
        })
    }
}

class Instances {

     all() {
        return new Promise((resolve, reject) => {
            Api.get('instances')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    show(instanceToken:string) {
        return new Promise((resolve, reject) => {
            Api.get('instances/' + instanceToken + '/show')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    updateConfigs(instanceToken:string, configs:any) {
        return new Promise((resolve, reject) => {
            Api.put('instances/' + instanceToken + '/configs', {
                display_name: configs.display_name,
                block_disconnection: configs.block_disconnection,
            })
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    getToken() {
        return new Promise((resolve, reject) => {
            Api.post('instances/token')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    destroy(instanceToken:string) {
        return new Promise((resolve, reject) => {
            Api.delete('instances/' + instanceToken + '/destroy')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

}

class Schedules {
    paginate(page = 1) {
        return new Promise((resolve, reject) => {
            Api.get('schedules?page=' + page)
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    show(schedule:number) {
        return new Promise((resolve, reject) => {
            Api.get('schedules/' + schedule)
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    create(obj:any) {
        return new Promise((resolve, reject) => {
            Api
                .post('schedules', obj)
                .then(resolve)
                .catch(reject);
        })
    }

    forSync(instanceToken:string, chat:string, obj:any) {
        return new Promise((resolve, reject) => {
            Api
                .post('schedules/' + instanceToken + '/' + chat + '/new-sync', obj)
                .then((res:any) => resolve(res.data))
                .catch(reject);
        })
    }

    destroy(id:number) {
        return new Promise((resolve, reject) => {
            Api.delete('schedules/' + id)
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }
}

class Chats {

    discover(instanceToken:string, messageId:string) {
        return new Promise((resolve, reject) => {
            Api.get('chats/' + instanceToken + '/messages/' + messageId + '/discover')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    paginate(instanceToken:string, page = 1) {
        return new Promise((resolve, reject) => {
            Api.get('chats/' + instanceToken + '?page=' + page)
                .then((res:any) => {
                    resolve(res?.data || []);
                })
                .catch(reject);
        });
    }

    messages(instanceToken:string, chatToken:string, page = 1) {
        return new Promise((resolve, reject) => {
            Api.get('chats/' + instanceToken + '/messages/' + chatToken + '?page=' + page)
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

}

class Media {

    mountLoadUrl(instanceToken:string, messageId:string, quoted = '') {
        const token = localStorage.getItem('jwt');

        return Api.fullUrl(`media/${instanceToken}/message/${messageId}/download?quoted=${quoted}&token=${token}`);
    }

}

class Account {

    async registerEvent(name:string, data:any = null) {
        await Api.post('account/register-event', {
            name,
            data
        });
    }

    get() {
        return new Promise((resolve, reject) => {
            Api.get('account')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    checkCEP(cep:string) {
        return new Promise((resolve, reject) => {
            Api.get('account/check-cep?cep=' + cep)
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    update(data:any) {
        return new Promise((resolve, reject) => {
            Api.put('account', data)
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    updateAddress(data:any) {
        return new Promise((resolve, reject) => {
            Api.put('account/update-address', data)
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    markRedirectPageViewed() {
        return new Promise((resolve, reject) => {
            Api.put('account/redirect-viewed')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

}

class Plans {

    byPeriod(period = 1) {
        return new Promise((resolve, reject) => {
            Api.get('plans?period=' + period)
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    byPeriods() {
        return new Promise((resolve, reject) => {
            Api.get('plans-by-periods')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    show(planSlug:string) {
        return new Promise((resolve, reject) => {
            Api.get(`plans/${planSlug}/show`)
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    paymentMethods(plan:string) {
        return new Promise((resolve, reject) => {
            Api.get('plans/' + plan + '/payment-methods')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    bankAccounts(planSlug:string) {
        return new Promise((resolve, reject) => {
            Api.get('plans/' + planSlug + '/bank-accounts')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

}

class Pagarme {

    payWithCard(planSlug:string, card:any) {
        return new Promise((resolve, reject) => {
            Api.post('payments/pagarme/pagarme_cc/' + planSlug, card)
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    generateBoleto(planSlug:string, document_number:string) {
        return new Promise((resolve, reject) => {
            Api.post('payments/pagarme/pagarme_boleto/' + planSlug, {document_number})
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

}

class Iugu {

    generateBoleto(planSlug:string, document_number:string, zipcode:string, number:string) {
        return new Promise((resolve, reject) => {
            Api.post('payments/iugu/iugu_boleto/' + planSlug, {
                document_number,
                zipcode,
                number
            })
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

}

class Stripe {

    pay(planSlug:string, paymentMethodId:string) {
        return new Promise((resolve, reject) => {
            Api.post('payments/stripe/' + planSlug + '/pay', {
                payment_method_id: paymentMethodId
            })
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

}

class Paymee {

    pay(planSlug:string, document_number:string) {
        return new Promise((resolve, reject) => {
            Api.post('payments/paymee/' + planSlug + '/pay', {document_number})
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    payPix(planSlug:string, document_number:string) {
        return new Promise((resolve, reject) => {
            Api.post('payments/paymee/' + planSlug + '/pay-pix', {document_number})
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    checkPixStatus() {
        return new Promise((resolve, reject) => {
            Api.get('payments/paymee/pix/status')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

}

class Payments {

    pagarme() {
        return new Pagarme();
    }

    iugu() {
        return new Iugu();
    }

    stripe() {
        return new Stripe();
    }

    paymee() {
        return new Paymee();
    }
}

class Subscription {

    cancelPending() {
        return new Promise((resolve, reject) => {
            Api.put('subscription/cancel-pending')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    pendingActions() {
        return new Promise((resolve, reject) => {
            Api.get('subscription/pending-actions')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    schedulesUsage() {
        return new Promise((resolve, reject) => {
            Api.get('subscription/schedules-usage')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

}

class Zapin extends Api {

    static instances() {
        return new Instances();
    }

    static schedules() {
        return new Schedules();
    }

    static chats() {
        return new Chats();
    }

    static media() {
        return new Media();
    }

    static account() {
        return new Account();
    }

    static auth() {
        return new Auth();
    }

    static plans() {
        return new Plans();
    }

    static payments() {
        return new Payments();
    }

    static subscription() {
        return new Subscription();
    }

    static updateUserData() {
        return Auth.me(true);
    }

    static configs() {
        return new Promise((resolve, reject) => {
            Api.get('frontend/configs')
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

}

class User {

    public id: number;
    public timezone: string;
    public name: string;
    public email: string;
    public country: string;
    public language: string;
    public redirect_page: string;
    public subscription: any;

    constructor(user: any) {
        this.id = user.id;
        this.name = user.name;
        this.email = user.email;
        this.country = user.country.toUpperCase();
        this.language = user.language;
        this.timezone = user.timezone;
        this.redirect_page = user.redirect_page;
        this.subscription = user.subscription;
    }

    hasSubscription() {
        return this.subscription && typeof this.subscription === 'object';
    }

    subscriptionIsActive() {
        return this.subscription && this.subscription.is_active;
    }

    hasValidSubscription() {
        return this.hasSubscription() && this.subscriptionIsActive();
    }

    account() {
        return new Account();
    }

}

class Auth extends Api {

    register(user:any) {
        return new Promise((resolve, reject) => {
            if(window.appConfig?.revenue?.id) {
                user.revenue_id = window.appConfig?.revenue?.id;
            }

            Api.post('auth/register', user)
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    passwordRecover(email:string) {
        return new Promise((resolve, reject) => {
            Api.post('auth/password-recover', {email})
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    passwordReset(data:any) {
        return new Promise((resolve, reject) => {
            Api.put('auth/password-recover', data)
                .then((res:any) => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    refreshToken(): Promise<string> {
        return new Promise((resolve, reject) => {
            Api.post('auth/refresh')
                .then((res:any) => {
                    console.log('new token', res.data.data.token)
                    Auth.setJwt(res.data.data.token);
                    resolve(res.data.data.token);
                })
                .catch(reject);
        });
    }

    static getJwt(){
        if(localStorage.getItem('jwt')) {
            return localStorage.getItem('jwt');
        }

        return null;
    }

    static setJwt(token:string) {
        localStorage.setItem('jwt', token);
        document.cookie = 'X-Authorization=' + token + '; path=/';
    }

    static login(email:string, password:string) {
        return new Promise((resolve, reject) => {
            const data:any = { email, password };

            if(window.appConfig?.revenue?.id) {
                data.revenue_id = window.appConfig?.revenue?.id;
            }

            this.post('auth/login', data)
                .then((res:any) => {
                    if(res.data.data.token) {
                        this.setJwt(res.data.data.token);

                        this.me(true)
                            .then(res => {
                                Auth.setAppLocaleByCurrentUser();

                                resolve(res);
                            })
                            .catch(reject)
                    } else {
                        reject();
                    }
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                })
        })
    }

    static setCountry() {
        return new Promise((resolve) => {
            const user = Auth.currentUser();

            if(user && user.country) {
                window.country = user.country;

                resolve(true);
            } else {
                window.country = this.getParameterByName('country');

                this.get('geo?country=' + (window.country || ''))
                    .then((res:any) => {
                        window.country = res.data.data.iso_code.toLowerCase();
                        window.locale = res.data.data.configs.lang.replace('_', '-');
                    })
                    .finally(() => resolve(true))
            }
        });
    }

    static setAppLocaleByCurrentUser() {
        return new Promise((resolve) => {
            this.setCountry()
                .finally(async () => {
                    const user = Auth.currentUser();

                    if(user && user.language) {
                        await setI18nLocale(i18n, user.language.replace('_', '-'));
                    }

                    resolve(true);
                });
        })
    }

    static me(updateLocalStorageInfo = false) {
        return new Promise((resolve, reject) => {
            Api.get('auth/me')
                .then((res:any) => {
                    const user = res.data.data;

                    Sentry.setUser({
                        id: user.id,
                        username: user.name,
                        email: user.email,
                    });

                    if(updateLocalStorageInfo) {
                        localStorage.setItem('user', JSON.stringify(new User(user)));

                        // if(!localStorage.getItem('jwt')) {
                        //     localStorage.setItem('jwt', 'session');
                        // }
                    }

                    this.setAppLocaleByCurrentUser()
                        .finally(() => resolve(res.data.data));
                })
                .catch((err) => {
                    if(updateLocalStorageInfo) {
                        this.clearUserData();
                    }

                    reject(err);
                })
        })
    }

    static clearUserData() {
        localStorage.removeItem('user');
        localStorage.removeItem('jwt');
    }

    static logout() {
        this.clearUserData();

        this.post('auth/logout')
            .then(() => {
                router.push({
                    name: 'Login'
                })
            })
            .catch(() => {
                router.push({
                    name: 'Login'
                })
            })
    }

    static currentUser() {
        try {
            let user = localStorage.getItem('user');

            if(!user) {
                return null;
            }

            user = JSON.parse(user);

            return new User(user);
        } catch (e) {
            return null;
        }
    }
}

export { Api, Auth, Zapin, User };
